import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementBlock as _createElementBlock, createVNode as _createVNode, resolveComponent as _resolveComponent, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  class: "common-filter-container flex flex-col gap-4 pb-4 [&_.v-field]:text-sm [&_.v-field]:[--v-field-padding-start:8px] [&_.v-field]:[--v-field-padding-end:4px] [&_.v-field\\_\\_outline]:!text-gray-300 [&_input::placeholder]:!opacity-100 [&_div:has(>.v-checkbox)]:flex-row-reverse [&_div:has(>.v-checkbox)]:justify-between",
  "data-cy": "report-filter-panel"
}
const _hoisted_2 = { key: 1 }
const _hoisted_3 = {
  key: 2,
  class: "grid grid-cols-2 gap-2"
}

import { computed, reactive, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { format } from 'date-fns';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlPracticeAreaSelect from '@/components/LlPracticeAreaSelect.vue';
import LlSelect from '@/components/LlSelect.vue';
import MatterInvolvingFilter from '@/components/MatterInvolvingFilter.vue';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useReportFiltersStore } from '@/stores/reports/useReportFiltersStore';
import type { ReportUser } from '@/models/reportFilterTypes';


export default /*@__PURE__*/_defineComponent({
  ...{ inheritAttrs: false },
  __name: 'ReportFilterPanel',
  setup(__props) {



const store = useReportFiltersStore();
const { filters, panel } = storeToRefs(store);
const { features } = storeToRefs(useMeStore());

const show = computed(() => panel.value.show);

const showDistribution = computed(() =>
  Boolean(show.value.distribution && features.value?.distribution),
);

const distributionLocksPeriod = computed(
  () => showDistribution.value && store.hasDistribution,
);

// The generic engine hides billed status under a distribution; standard reports instead
// tell their controllers via the payload's `show.billed`.
const distributionHidesBilled = computed(
  () => panel.value.family === 'generic' && distributionLocksPeriod.value,
);

const distributionItems = computed(() =>
  panel.value.distributions.map((d) => ({
    id: d.id,
    title: `${d.label} ( ${format(new Date(d.date), 'MM/dd/yy')} )`,
  })),
);

const selectedDistribution = computed(() =>
  distributionItems.value.find((d) => d.id === filters.value.distributionId),
);

function setDistribution(item: { id: string } | null | undefined) {
  store.setFilters({ distributionId: item?.id ?? null });
}

const involvingForm = reactive({
  scopeAccount: filters.value.scopeAccount as ReportUser | null,
  primary: filters.value.primary,
  originating: filters.value.originating,
  reviewer: filters.value.reviewer,
});

watch(
  () => [
    filters.value.scopeAccount,
    filters.value.primary,
    filters.value.originating,
    filters.value.reviewer,
  ],
  ([scopeAccount, primary, originating, reviewer]) => {
    Object.assign(involvingForm, {
      scopeAccount,
      primary,
      originating,
      reviewer,
    });
  },
);

function onMatterInvolvingChange(form: typeof involvingForm) {
  const { scopeAccount, primary, originating, reviewer } = form;
  // MatterInvolvingFilter unticks the role boxes when the user is cleared; the reports
  // always kept them, so picking a user again restores the previous roles.
  if (scopeAccount) store.setFilters({ primary, originating, reviewer });
  // Through the action, so a deliberate "no filter" is never overwritten by the default.
  if ((scopeAccount?.id ?? null) !== (filters.value.scopeAccount?.id ?? null)) {
    store.setScopeAccount(scopeAccount ?? null);
  }
  Object.assign(involvingForm, {
    scopeAccount: filters.value.scopeAccount,
    primary: filters.value.primary,
    originating: filters.value.originating,
    reviewer: filters.value.reviewer,
  });
}

return (_ctx: any,_cache: any) => {
  const _component_v_divider = _resolveComponent("v-divider")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showDistribution.value)
      ? (_openBlock(), _createBlock(LlSelect, {
          key: 0,
          label: "Distribution",
          "label-class": "py-1 text-sm",
          "model-value": selectedDistribution.value,
          items: distributionItems.value,
          "item-title": "title",
          placeholder: "Distribution",
          clearable: "",
          "hide-details": "",
          "data-cy": "report-filter-distribution",
          "onUpdate:modelValue": setDistribution
        }, null, 8, ["model-value", "items"]))
      : _createCommentVNode("", true),
    (show.value.dateRange)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (!distributionLocksPeriod.value)
            ? (_openBlock(), _createBlock(LlSelect, {
                key: 0,
                label: "Time Period",
                "label-class": "py-1 text-sm",
                "model-value": _unref(filters).dateRange ?? undefined,
                items: _unref(store).periodOptions,
                "item-value": "label",
                placeholder: "Select Time Period",
                "hide-details": "",
                "data-cy": "report-filter-period",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_unref(store).setFilters({ dateRange: $event })))
              }, null, 8, ["model-value", "items"]))
            : (_openBlock(), _createBlock(LlSelect, {
                key: 1,
                label: "Time Period",
                "label-class": "py-1 text-sm",
                items: [],
                placeholder: "Distribution",
                disabled: "",
                "hide-details": ""
              }))
        ]))
      : _createCommentVNode("", true),
    (
        show.value.dateRange && _unref(filters).dateRange?.custom && !distributionLocksPeriod.value
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(LlDatePicker, {
            label: "Start",
            "label-class": "py-1 text-sm",
            "model-value": _unref(filters).dateRange.range.startDate,
            "show-button": false,
            "data-cy": "report-filter-start",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_unref(store).setCustomDate('startDate', $event)))
          }, null, 8, ["model-value"]),
          _createVNode(LlDatePicker, {
            label: "End",
            "label-class": "py-1 text-sm",
            "model-value": _unref(filters).dateRange.range.endDate,
            "show-button": false,
            "data-cy": "report-filter-end",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_unref(store).setCustomDate('endDate', $event)))
          }, null, 8, ["model-value"])
        ]))
      : _createCommentVNode("", true),
    (show.value.matterForUser)
      ? (_openBlock(), _createBlock(MatterInvolvingFilter, {
          key: 3,
          horizontal: "",
          form: involvingForm,
          accounts: _unref(panel).accounts,
          "lock-account-selection": _unref(panel).lockAccountSelection,
          onMatterInvolvingChange: onMatterInvolvingChange
        }, null, 8, ["form", "accounts", "lock-account-selection"]))
      : _createCommentVNode("", true),
    (show.value.archivedStatus || show.value.matterForUser)
      ? (_openBlock(), _createBlock(LlSelect, {
          key: 4,
          label: "Show Archived",
          "label-class": "py-1 text-sm",
          "model-value": _unref(filters).status ?? undefined,
          items: _unref(store).archivedOptions,
          "hide-details": "",
          "data-cy": "report-filter-archived",
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_unref(store).setFilters({ status: $event })))
        }, null, 8, ["model-value", "items"]))
      : _createCommentVNode("", true),
    (show.value.matterTags)
      ? (_openBlock(), _createBlock(LlPracticeAreaSelect, {
          key: 5,
          "model-value": _unref(filters).matterTag,
          placeholder: "Filter by Practice Area",
          clearable: "",
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_unref(store).setFilters({ matterTag: $event ?? null })))
        }, null, 8, ["model-value"]))
      : _createCommentVNode("", true),
    (show.value.billed && !distributionHidesBilled.value)
      ? (_openBlock(), _createBlock(LlSelect, {
          key: 6,
          label: "Show Billed",
          "label-class": "py-1 text-sm",
          "model-value": _unref(filters).billed ?? undefined,
          items: _unref(store).billedOptions,
          "hide-details": "",
          "data-cy": "report-filter-billed",
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_unref(store).setFilters({ billed: $event })))
        }, null, 8, ["model-value", "items"]))
      : _createCommentVNode("", true),
    (show.value.contingencyBilled)
      ? (_openBlock(), _createBlock(LlSelect, {
          key: 7,
          label: "Show Billed",
          "label-class": "py-1 text-sm",
          "model-value": _unref(filters).contingencyBilled ?? undefined,
          items: _unref(store).billedOptions,
          "hide-details": "",
          "data-cy": "report-filter-contingency-billed",
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_unref(store).setFilters({ contingencyBilled: $event })))
        }, null, 8, ["model-value", "items"]))
      : _createCommentVNode("", true),
    (show.value.trustAccount && _unref(panel).trustAccounts.length > 1)
      ? (_openBlock(), _createBlock(LlSelect, {
          key: 8,
          label: "Select Account",
          "label-class": "py-1 text-sm",
          "model-value": _unref(filters).trustAccount ?? undefined,
          items: _unref(panel).trustAccounts,
          "item-title": "name",
          "hide-details": "",
          "data-cy": "report-filter-trust-account",
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => (_unref(store).setTrustAccount($event)))
        }, null, 8, ["model-value", "items"]))
      : _createCommentVNode("", true),
    (show.value.itemType)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 9 }, [
          (show.value.matterForUser || show.value.dateRange)
            ? (_openBlock(), _createBlock(_component_v_divider, { key: 0 }))
            : _createCommentVNode("", true),
          _createVNode(LlCheckbox, {
            "model-value": _unref(filters).showEntries,
            label: "Show Time",
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => (_unref(store).setFilters({ showEntries: $event })))
          }, null, 8, ["model-value"]),
          _createVNode(LlCheckbox, {
            "model-value": _unref(filters).showCharges,
            label: "Show Fixed Fees",
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => (_unref(store).setFilters({ showCharges: $event })))
          }, null, 8, ["model-value"]),
          _createVNode(LlCheckbox, {
            "model-value": _unref(filters).showExpenses,
            label: "Show Expenses",
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => (_unref(store).setFilters({ showExpenses: $event })))
          }, null, 8, ["model-value"])
        ], 64))
      : _createCommentVNode("", true),
    (show.value.includeBilled)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 10 }, [
          _createVNode(_component_v_divider),
          _createVNode(LlCheckbox, {
            "model-value": _unref(filters).includeBilled,
            label: "Include Billed",
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => (_unref(store).setFilters({ includeBilled: $event })))
          }, null, 8, ["model-value"])
        ], 64))
      : _createCommentVNode("", true),
    (show.value.originationUser)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 11 }, [
          _createVNode(_component_v_divider),
          _createVNode(LlCheckbox, {
            "model-value": _unref(filters).originationUser,
            label: "Group by Users",
            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => (_unref(store).setFilters({ originationUser: $event })))
          }, null, 8, ["model-value"])
        ], 64))
      : _createCommentVNode("", true),
    (show.value.showExpense)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 12 }, [
          _createVNode(_component_v_divider),
          _createVNode(LlCheckbox, {
            "model-value": _unref(filters).showExpense,
            label: "Show Expenses",
            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => (_unref(store).setFilters({ showExpense: $event })))
          }, null, 8, ["model-value"])
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})